import { validateNumeric2DArray, validateFitted } from '../utils/validation.js';
import { mean, standardDeviation, min, max, median, quantile } from '../utils/stats.js';

export class StandardScaler {
  constructor(options = {}) {
    this.withMean = options.withMean !== undefined ? options.withMean : true;
    this.withStd = options.withStd !== undefined ? options.withStd : true;
    
    this.mean = null;
    this.std = null;
    this.isFitted = false;
  }

  fit(X) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    
    const nFeatures = X[0].length;
    this.mean = Array(nFeatures).fill(0);
    this.std = Array(nFeatures).fill(1);
    
    for (let j = 0; j < nFeatures; j++) {
      const column = X.map(row => row[j]);
      
      if (this.withMean) {
        this.mean[j] = mean(column);
      }
      
      if (this.withStd) {
        this.std[j] = standardDeviation(column) || 1;
      }
    }
    
    this.isFitted = true;
    return this;
  }

  transform(X) {
    validateFitted(this.isFitted, 'StandardScaler');
    
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    
    return X.map(row => 
      row.map((val, j) => (val - this.mean[j]) / this.std[j])
    );
  }

  fitTransform(X) {
    this.fit(X);
    return this.transform(X);
  }

  inverseTransform(X) {
    validateFitted(this.isFitted, 'StandardScaler');
    
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    
    return X.map(row => 
      row.map((val, j) => val * this.std[j] + this.mean[j])
    );
  }
}

export class MinMaxScaler {
  constructor(options = {}) {
    this.featureRange = options.featureRange || [0, 1];
    
    this.dataMin = null;
    this.dataMax = null;
    this.dataRange = null;
    this.isFitted = false;
  }

  fit(X) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    
    const nFeatures = X[0].length;
    this.dataMin = Array(nFeatures);
    this.dataMax = Array(nFeatures);
    this.dataRange = Array(nFeatures);
    
    for (let j = 0; j < nFeatures; j++) {
      const column = X.map(row => row[j]);
      this.dataMin[j] = min(column);
      this.dataMax[j] = max(column);
      this.dataRange[j] = this.dataMax[j] - this.dataMin[j] || 1;
    }
    
    this.isFitted = true;
    return this;
  }

  transform(X) {
    validateFitted(this.isFitted, 'MinMaxScaler');
    
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    
    const [minRange, maxRange] = this.featureRange;
    const scale = maxRange - minRange;
    
    return X.map(row => 
      row.map((val, j) => {
        const normalized = (val - this.dataMin[j]) / this.dataRange[j];
        return normalized * scale + minRange;
      })
    );
  }

  fitTransform(X) {
    this.fit(X);
    return this.transform(X);
  }

  inverseTransform(X) {
    validateFitted(this.isFitted, 'MinMaxScaler');
    
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    
    const [minRange, maxRange] = this.featureRange;
    const scale = maxRange - minRange;
    
    return X.map(row => 
      row.map((val, j) => {
        const normalized = (val - minRange) / scale;
        return normalized * this.dataRange[j] + this.dataMin[j];
      })
    );
  }
}

export class RobustScaler {
  constructor(options = {}) {
    this.quantileRange = options.quantileRange || [25, 75];
    this.withCentering = options.withCentering !== undefined ? options.withCentering : true;
    this.withScaling = options.withScaling !== undefined ? options.withScaling : true;
    
    this.center = null;
    this.scale = null;
    this.isFitted = false;
  }

  fit(X) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    
    const nFeatures = X[0].length;
    this.center = Array(nFeatures).fill(0);
    this.scale = Array(nFeatures).fill(1);
    
    for (let j = 0; j < nFeatures; j++) {
      const column = X.map(row => row[j]);
      
      if (this.withCentering) {
        this.center[j] = median(column);
      }
      
      if (this.withScaling) {
        const q1 = quantile(column, this.quantileRange[0] / 100);
        const q3 = quantile(column, this.quantileRange[1] / 100);
        this.scale[j] = (q3 - q1) || 1;
      }
    }
    
    this.isFitted = true;
    return this;
  }

  transform(X) {
    validateFitted(this.isFitted, 'RobustScaler');
    
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    
    return X.map(row => 
      row.map((val, j) => (val - this.center[j]) / this.scale[j])
    );
  }

  fitTransform(X) {
    this.fit(X);
    return this.transform(X);
  }

  inverseTransform(X) {
    validateFitted(this.isFitted, 'RobustScaler');
    
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }
    
    validateNumeric2DArray(X, 'X');
    
    return X.map(row => 
      row.map((val, j) => val * this.scale[j] + this.center[j])
    );
  }
}
